import { ACCESSORY_PRODUCT_SUBTYPE_CATALOGS } from './catalog-accessories';
import { APPAREL_PRODUCT_SUBTYPE_CATALOGS } from './catalog-apparel';
import { HOME_PRODUCT_SUBTYPE_CATALOGS } from './catalog-home';
import { OBJECT_PRODUCT_SUBTYPE_CATALOGS } from './catalog-objects';
import { VEHICLE_PRODUCT_SUBTYPE_CATALOGS } from './catalog-vehicles';
import type {
  ProductPresetSubtype,
  ProductPresetSubtypeCatalog,
} from './types';

export * from './types';

export const PRODUCT_PRESET_SUBTYPE_CATALOGS: readonly ProductPresetSubtypeCatalog[] =
  [
    ...APPAREL_PRODUCT_SUBTYPE_CATALOGS,
    ...ACCESSORY_PRODUCT_SUBTYPE_CATALOGS,
    ...HOME_PRODUCT_SUBTYPE_CATALOGS,
    ...OBJECT_PRODUCT_SUBTYPE_CATALOGS,
    ...VEHICLE_PRODUCT_SUBTYPE_CATALOGS,
  ];

function normalizeParentName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

const CATALOG_BY_PARENT_NAME = new Map(
  PRODUCT_PRESET_SUBTYPE_CATALOGS.flatMap((catalog) =>
    catalog.parentNames.map(
      (name) => [normalizeParentName(name), catalog] as const
    )
  )
);

export function getProductPresetSubtypeCatalog(
  parentName: string
): ProductPresetSubtypeCatalog | null {
  return CATALOG_BY_PARENT_NAME.get(normalizeParentName(parentName)) ?? null;
}

export function getProductPresetSubtype(
  parentName: string,
  subtypeId: string
): ProductPresetSubtype | null {
  const catalog = getProductPresetSubtypeCatalog(parentName);
  return catalog?.subtypes.find((subtype) => subtype.id === subtypeId) ?? null;
}

export function getProductPresetSubtypeByLabel(
  parentName: string,
  subtypeLabel: string
): ProductPresetSubtype | null {
  const catalog = getProductPresetSubtypeCatalog(parentName);
  const normalizedLabel = subtypeLabel.trim().toLowerCase();
  return (
    catalog?.subtypes.find(
      (subtype) => subtype.label.toLowerCase() === normalizedLabel
    ) ?? null
  );
}

export function parseProductPresetSubtypeDisplayLabel(label: string): {
  parentName: string;
  subtype: ProductPresetSubtype;
} | null {
  const separatorIndex = label.indexOf(' · ');
  if (separatorIndex < 1) return null;
  const parentName = label.slice(0, separatorIndex).trim();
  const subtypeLabel = label.slice(separatorIndex + 3).trim();
  const subtype = getProductPresetSubtypeByLabel(parentName, subtypeLabel);
  return subtype ? { parentName, subtype } : null;
}

export function formatProductPresetSubtypeLabel(
  parentName: string,
  subtypeId: string,
  displayParentName = parentName,
  displaySubtypeName?: string
): string {
  const subtype = getProductPresetSubtype(parentName, subtypeId);
  return subtype
    ? `${displayParentName} · ${displaySubtypeName ?? subtype.label}`
    : displayParentName;
}
